// Meta Ads › Hesap Sağlığı: Poyais One'ın şeffaf sağlık skoru (bileşenleriyle) + teknik durum
// (piksel, öğrenme aşaması, bütçe dağılımı, atıf) + geçen yılla karşılaştırma + Meta'nın kendi
// Fırsat Puanı ve önerileri (Genel Bakış'tan buraya taşındı).
// Backend: GET /analysis/meta/{clientId}/health (yalnızca okuma).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { T, MONO } from '../poyais-ui/theme';
import { useLang, addTranslations } from '../i18n';
import { MetaOpportunityCard } from '../MetaOpportunity';
import { Panel, Loading, Muted, money, num, roasTxt, useMobile } from './ui';

addTranslations({
  'Hesap Sağlığı Skoru': 'Account health score', 'Skor, aşağıdaki ölçülebilen bileşenlerin ağırlıklı ortalamasıdır; kesin bir ölçüm değil, nereye bakman gerektiğini gösteren bir özettir.':
    'The score is a weighted average of the measurable components below; it is not an exact measurement but a summary of where to look.',
  'Günlük bütçesi': 'Ad sets with a daily budget below', '’nin altında olan reklam seti': '', '’nin altında olan aktif reklam seti yok.': ' — none among active ad sets.', 'Çok küçük bütçeler Meta’nın öğrenmesini zorlaştırır.': 'Very small budgets make Meta’s learning harder.',
  'Ağırlık': 'Weight', 'Piksel': 'Pixel', 'Son olay': 'Last event', 'saat önce': 'hours ago', 'Piksel bulunamadı.': 'No pixel found.',
  'Öğrenme Aşaması': 'Learning phase', 'Aktif reklam setlerinin Meta’daki öğrenme durumu.': 'Learning status of active ad sets in Meta.',
  'Tamamlandı': 'Completed', 'Öğreniyor': 'Learning', 'Öğrenme sınırlı': 'Learning limited', 'Bilinmiyor': 'Unknown',
  'Bütçe Dağılımı': 'Budget split', 'Aktif reklam seti': 'Active ad sets', 'Aktif kampanya': 'Active campaigns', 'Kampanya başına reklam seti': 'Ad sets per campaign',
  'günlük bütçesi düşük reklam seti': 'ad sets with a low daily budget', 'altı': 'below',
  'Atıf Dağılımı': 'Attribution split', 'Satın almaların hangi atıf penceresinden geldiği.': 'Which attribution window purchases came from.',
  '1 gün tıklama': '1-day click', '7 gün tıklama': '7-day click', '1 gün görüntüleme': '1-day view', 'Görüntüleme payı': 'View-through share',
  'Geçen Yılla Karşılaştırma': 'Year-over-year', 'Bu dönem': 'This period', 'Geçen yıl': 'Last year', 'Değişim': 'Change',
  'Harcama': 'Spend', 'Satın alma': 'Purchases', 'Satın Alma': 'Purchases', 'Satın Alma Değeri': 'Purchase Value', 'Satın alma değeri': 'Purchase value', 'Sağlık verisi alınamadı.': 'Health data could not be loaded.',
  'Meta’nın kendi değerlendirmesi': 'Meta’s own assessment',
  'Verimlilik (ROAS, Geçen Yıla Göre)': 'Efficiency (ROAS vs Last Year)', 'Atıf Kalitesi (Görüntüleme Payı)': 'Attribution quality (view-through share)',
  'Piksel Sağlığı': 'Pixel health', 'Bütçe Bölünmesi': 'Fragmentation (budget split)',
});

export const tone = (s: number | null | undefined) => s === null || s === undefined ? T.textDim : s >= 80 ? T.greenText : s >= 55 ? T.amberText : T.redText;
export const fill = (s: number | null | undefined) => s === null || s === undefined ? T.trackBg : s >= 80 ? T.green : s >= 55 ? T.amber : T.red;

export function Ring({ score }: { score: number | null }) {
  const r = 46, c = 2 * Math.PI * r, p = score === null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  return (
    <svg width="116" height="116" viewBox="0 0 116 116" role="img" aria-label={`${score ?? '—'} / 100`}>
      <circle cx="58" cy="58" r={r} fill="none" stroke="var(--py-track-bg)" strokeWidth="10" />
      <circle cx="58" cy="58" r={r} fill="none" stroke={fill(score)} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${c * p} ${c}`} transform="rotate(-90 58 58)" />
      <text x="58" y="62" textAnchor="middle" style={{ fontSize: 30, fontWeight: 800, fill: 'var(--py-text)', fontFamily: 'Manrope, sans-serif' }}>{score ?? '—'}</text>
      <text x="58" y="80" textAnchor="middle" style={{ fontSize: 10, fill: 'var(--py-text-dim)' }}>/ 100</text>
    </svg>
  );
}

export function Stat({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div style={{ background: T.subtleBg, borderRadius: 12, padding: '10px 12px' }}>
      <div style={{ fontSize: 11.5, color: T.textMidAlt, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, fontFamily: MONO, color: color || T.text, marginTop: 4 }}>{value}</div>
    </div>
  );
}


export function ScoreCard({ d }: { d: any }) {
  const { t } = useLang();
  const isMobile = useMobile();
  return (
    <Panel>
            <div style={{ display: 'flex', gap: 24, alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row' }}>
              <Ring score={d.skor ?? null} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{t('Hesap Sağlığı Skoru')}</div>
                <div style={{ fontSize: 12.5, color: T.textMidAlt, marginTop: 6, lineHeight: 1.55, maxWidth: 640 }}>{t('Skor, aşağıdaki ölçülebilen bileşenlerin ağırlıklı ortalamasıdır; kesin bir ölçüm değil, nereye bakman gerektiğini gösteren bir özettir.')}</div>
              </div>
            </div>
            <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(d.bilesenler || []).map((b: any) => (
                <div key={b.ad} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(220px, 1.2fr) 2fr 52px', gap: isMobile ? 6 : 16, alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{t(b.ad)}<span style={{ fontWeight: 600, color: T.textDim }}> - %{num((b.agirlik || 0) * 100)} {t('Ağırlık')}</span></div>
                    <div style={{ fontSize: 11.5, color: T.textDim }}>{b.aciklama || ''}</div>
                  </div>
                  <div style={{ height: 8, background: T.trackBg, borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${b.puan ?? 0}%`, height: '100%', background: fill(b.puan), borderRadius: 999 }} /></div>
                  <div style={{ fontSize: 14, fontWeight: 800, fontFamily: MONO, color: tone(b.puan), textAlign: isMobile ? 'left' : 'right' }}>{b.puan ?? '—'}</div>
                </div>
              ))}
            </div>
    </Panel>
  );
}

const MON = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const fmtRange = (r?: string) => {
  const m = String(r || '').match(/(\d{4})-(\d{2})-(\d{2}) → (\d{4})-(\d{2})-(\d{2})/);
  return m ? `${Number(m[3])} ${MON[Number(m[2]) - 1]} ${m[1]} – ${Number(m[6])} ${MON[Number(m[5]) - 1]} ${m[4]}` : (r || '');
};

export function YoyTable({ yoy, cur }: { yoy: any; cur: string }) {
  const { t } = useLang();
  const chg = (a?: number, b?: number) => (a && b) ? `${a >= b ? '+' : ''}${num(((a - b) / b) * 100, 0)}%` : '—';
  if (!yoy) return null;
  return (
    <>
            <Panel pad={false} title={t('Geçen Yılla Karşılaştırma')} sub={yoy.not}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                  <thead style={{ background: T.subtleBg }}><tr>
                    {['', t('Bu dönem'), t('Geçen yıl'), t('Değişim')].map((h, i) => <th key={i} style={{ padding: '10px 18px', fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: T.textMidAlt, textAlign: i ? 'right' : 'left' }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {([['Harcama', 'harcama', (v: any) => money(v, cur)], ['Satın Alma', 'satin_alma', (v: any) => num(v)], ['Satın Alma Değeri', 'satin_alma_degeri', (v: any) => money(v, cur)], ['ROAS', 'roas', roasTxt]] as [string, string, (v: any) => string][]).map(([l, k, f]) => (
                      <tr key={k} style={{ borderTop: `1px solid ${T.tableDivider}` }}>
                        <td style={{ padding: '11px 18px', fontSize: 12.5, fontWeight: 700 }}>{t(l)}</td>
                        <td style={{ padding: '11px 18px', fontSize: 12.5, textAlign: 'right', fontFamily: MONO }}>{f(yoy.bu_donem?.[k])}</td>
                        <td style={{ padding: '11px 18px', fontSize: 12.5, textAlign: 'right', fontFamily: MONO, color: T.textMidAlt }}>{f(yoy.gecen_yil?.[k])}</td>
                        <td style={{ padding: '11px 18px', fontSize: 12.5, textAlign: 'right', fontFamily: MONO, fontWeight: 700 }}>{chg(yoy.bu_donem?.[k], yoy.gecen_yil?.[k])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '10px 18px 14px' }}><Muted>{t('Bu dönem')}: {fmtRange(yoy.bu_donem?.donem)} · {t('Geçen yıl')}: {fmtRange(yoy.gecen_yil?.donem)}</Muted></div>
            </Panel>
    </>
  );
}

export default function MetaHealth({ clientId, start, end, accountId, cur = 'TRY' }: { clientId: number; start: string; end: string; accountId?: string; cur?: string }) {
  const { t } = useLang();
  const isMobile = useMobile();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    setD(null); setErr('');
    axios.get(`/api/v1/analysis/meta/${clientId}/health?start_date=${start}&end_date=${end}`)
      .then(r => { if (alive) setD(r.data); }).catch(e => { if (alive) setErr(e?.response?.data?.detail || t('Sağlık verisi alınamadı.')); });
    return () => { alive = false; };
  }, [clientId, start, end]); // eslint-disable-line react-hooks/exhaustive-deps

  const learn = d?.ogrenme_asamasi || {};
  const frag = d?.parcalanma || {};
  const atf = d?.atif_dagilimi || {};
  const yoy = d?.yillik_karsilastirma;
  const chg = (a?: number, b?: number) => (a && b) ? `${a >= b ? '+' : ''}${num(((a - b) / b) * 100, 0)}%` : '—';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: 'Manrope, sans-serif' }}>
      {err && <Panel><div style={{ color: T.redText, fontSize: 13 }}>{err}</div></Panel>}
      {!d && !err && <Panel><Loading /></Panel>}
      {d && (
        <>
          <ScoreCard d={d} />

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16 }}>
            <Panel title={t('Öğrenme Aşaması')} sub={t('Aktif reklam setlerinin Meta’daki öğrenme durumu.')}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                <Stat label={t('Tamamlandı')} value={num(learn.tamamlandi)} color={T.greenText} />
                <Stat label={t('Öğreniyor')} value={num(learn.ogreniyor)} color={T.blueText} />
                <Stat label={t('Öğrenme sınırlı')} value={num(learn.ogrenme_sinirli)} color={learn.ogrenme_sinirli ? T.redText : undefined} />
                <Stat label={t('Bilinmiyor')} value={num(learn.bilinmiyor)} />
              </div>
            </Panel>
            <Panel title={t('Piksel')}>
              {(d.piksel || []).length === 0 ? <Muted>{t('Piksel bulunamadı.')}</Muted> : (d.piksel || []).map((p: any) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: p.saat_once !== null && p.saat_once <= 24 ? T.green : p.saat_once !== null && p.saat_once <= 72 ? T.amber : T.red, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{p.ad}</div>
                    <div style={{ fontSize: 11.5, color: T.textDim, fontFamily: MONO }}>{p.id}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11.5, color: T.textDim }}>{t('Son olay')}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{p.saat_once !== null && p.saat_once !== undefined ? `${num(p.saat_once, 0)} ${t('saat önce')}` : '—'}</div>
                  </div>
                </div>
              ))}
            </Panel>
            <Panel title={t('Bütçe Dağılımı')}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                <Stat label={t('Aktif kampanya')} value={num(frag.aktif_kampanya)} />
                <Stat label={t('Aktif reklam seti')} value={num(frag.aktif_reklam_seti)} />
                <Stat label={t('Kampanya başına reklam seti')} value={num(frag.kampanya_basina_ortalama, 1)} />
              </div>
              {frag.kucuk_butce_esigi_tl !== undefined && <div style={{ marginTop: 10 }}><Muted>
                {frag.kucuk_butceli_reklam_seti
                  ? <>{t('Günlük bütçesi')} {money(frag.kucuk_butce_esigi_tl, cur)}{t('’nin altında olan reklam seti')}: <b style={{ color: T.amberText }}>{(frag.kucuk_butceli_adlar || []).join(', ')}</b>. {t('Çok küçük bütçeler Meta’nın öğrenmesini zorlaştırır.')}</>
                  : <>{t('Günlük bütçesi')} {money(frag.kucuk_butce_esigi_tl, cur)}{t('’nin altında olan aktif reklam seti yok.')}</>}
              </Muted></div>}
            </Panel>
            <Panel title={t('Atıf Dağılımı')} sub={t('Satın almaların hangi atıf penceresinden geldiği.')}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                <Stat label={t('1 gün tıklama')} value={num(atf['1_gun_tiklama'])} />
                <Stat label={t('7 gün tıklama')} value={num(atf['7_gun_tiklama'])} />
                <Stat label={t('1 gün görüntüleme')} value={num(atf['1_gun_goruntuleme'])} />
                <Stat label={t('Görüntüleme payı')} value={atf.goruntuleme_payi_pct !== undefined && atf.goruntuleme_payi_pct !== null ? `%${num(atf.goruntuleme_payi_pct, 1)}` : '—'} />
              </div>
            </Panel>
          </div>

          <YoyTable yoy={yoy} cur={cur} />
          {(d.notlar || []).map((n: string, i: number) => <Panel key={i}><Muted>{n}</Muted></Panel>)}
        </>
      )}

      {/* Meta'nın kendi Fırsat Puanı ve önerileri — Genel Bakış'tan buraya taşındı */}
      {accountId && <MetaOpportunityCard accountId={accountId} />}
    </div>
  );
}
